import { on, reducer } from 'ts-action';
import * as ecgActions from '../../ecg/actions/ecg.actions';
import { WaveId } from '../types/wave.type';
import * as undoActions from '../../undo/actions/undo.actions';
import * as updateActions from '../actions/update.actions';
import { UpdateMode } from '../utils/update.utils';

declare module 'react-redux' {
  interface DefaultRootState {
    update: UpdateState;
  }
}

type UpdatedKeys = keyof Pick<DefaultUpdateState, 'onset' | 'offset'>;

interface BaseUpdateState {
  onset: number;
  offset: number;
  updateMode: null | UpdateMode;
  id?: WaveId;
}

export interface DefaultUpdateState extends BaseUpdateState {
  updateMode: null;
}

export interface UpdatingState extends BaseUpdateState {
  id: WaveId;
  previousTimestamp: number;
}

export interface BoundUpdateState extends UpdatingState {
  updateMode: 'boundary';
  boundary: UpdatedKeys;
}

export interface MoveUpdateState extends UpdatingState {
  updateMode: 'move';
}

export type UpdateState =
  | DefaultUpdateState
  | BoundUpdateState
  | MoveUpdateState;

const initialStateFactory = (): UpdateState => ({
  onset: 0,
  offset: 0,
  updateMode: null,
});

export default reducer(
  initialStateFactory(),
  on(ecgActions.clean, () => initialStateFactory()),
  on(
    updateActions.stop,
    (): UpdateState => ({
      onset: 0,
      offset: 0,
      updateMode: null,
    }),
  ),
  on(
    updateActions.setBoundary,
    (_state, { id, boundary, timestamp }): UpdateState => ({
      id,
      onset: 0,
      offset: 0,
      boundary,
      previousTimestamp: timestamp,
      updateMode: 'boundary',
    }),
  ),
  on(
    updateActions.setMoving,
    (_state, { id, timestamp }): UpdateState => ({
      id,
      onset: 0,
      offset: 0,
      previousTimestamp: timestamp,
      updateMode: 'move',
    }),
  ),
  on(updateActions.setTimestamp, (state, { timestamp }): UpdateState => {
    switch (state.updateMode) {
      case 'boundary':
        return {
          ...state,
          [state.boundary]: timestamp - state.previousTimestamp,
        };
      case 'move':
        return {
          ...state,
          onset: timestamp - state.previousTimestamp,
          offset: timestamp - state.previousTimestamp,
        };
      default:
        return state;
    }
  }),
  on(
    undoActions.undo,
    undoActions.restore,
    (state, { diff: { update } }): UpdateState => {
      if (!update) {
        return state;
      }
      return { ...state, ...update };
    },
  ),
);
